"use client"

import React from "react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import styled from "styled-components"
import { resetResponses, fetchAptitudeScore } from "modules/Assessments/actions"
import selectors from "modules/Assessments/selectors"
import Solutions from "./assessment-solution"

// Main Container
const Container = styled.div`
  width: 95%;
  max-width: 1400px;
  min-height: 500px;
  margin: 80px auto;
  padding: 30px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  position: relative;
  display: flex;
  flex-direction: column;
  font-family: 'Plus Jakarta Sans', sans-serif;
`

// Header Section
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`

const Title = styled.h1`
  font-size: 20px;
  font-weight: 500;
  color: #333333;
  margin: 0;
`

const AssessmentTimer = styled.div`
  padding: 12px 24px;
  background: #fff1f1;
  border-radius: 8px;
  color: #e53e3e;
  font-size: 16px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: 1px solid #e53e3e;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

// Top Section with Actions
const TopSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
`

const AssessmentName = styled.div`
  color: #666666;
  font-size: 16px;
`

const SolutionsButton = styled.button`
  padding: 12px 24px;
  background: #10b981;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: #059669;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`

// Results Section
const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
`

// Overall Score Cards (Top Blue Cards)
const OverallScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const ScoreCard = styled.div`
  background: #4285f4;
  color: white;
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(66, 133, 244, 0.2);
`

const ScoreValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1;
`

const ScoreLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  opacity: 0.9;
`

const ScoreSubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 4px;
`

// Summary Stats (Gray Cards)
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const SummaryCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 16px;
  border-radius: 8px;
  text-align: center;
`

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
`

const SummaryLabel = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

// Difficulty Analysis
const DifficultySection = styled.div`
  margin-bottom: 32px;
`

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const DifficultyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const DifficultyCard = styled.div`
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  border: 2px solid ${props => props.borderColor};
  background: ${props => props.bgColor};
`

const DifficultyLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
`

const DifficultyScore = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.color};
  margin-bottom: 8px;
`

const DifficultyPercentage = styled.div`
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`

// Table Components
const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`
const NegativeMarkingInfo = styled.div`
  font-size: 13px;
  color: #dc2626;
  font-weight: 500;
  padding: 4px 8px;
  background: #fef2f2;
  border-radius: 4px;
  border: 1px solid #fee2e2;
`


const SectionHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
  padding: 0;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`

const TableHeader = styled.thead`
  background: #f9fafb;
`

const TableHeaderCell = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const TableBody = styled.tbody``

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #f9fafb;
  }
  
  &:hover {
    background: #f3f4f6;
  }
`

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  color: #374151;
  
  &:last-child {
    border-bottom: none;
  }
`

const ScoreCell = styled(TableCell)`
  font-weight: 600;
  color: #1f2937;
`

const PercentageCell = styled(TableCell)`
  font-weight: 600;
  color: ${props => {
    const percentage = parseInt(props.percentage) || 0;
    if (percentage >= 80) return '#10b981';
    if (percentage >= 60) return '#f59e0b';
    if (percentage >= 40) return '#ef4444';
    return '#6b7280';
  }};
`

// Loading Components
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  text-align: center;
`

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
  font-weight: 500;
`

// Error Components
const ErrorContainer = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 24px;
  margin: 20px 0;
  text-align: center;
`

const ErrorTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 8px;
`

const ErrorMessage = styled.div`
  font-size: 14px;
  color: #7f1d1d;
  line-height: 1.5;
  margin-bottom: 16px;
`

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`

// Skeleton Components
const SkeletonOverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const SkeletonScoreCard = styled.div`
  background: #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`

const SkeletonSummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const SkeletonSummaryCard = styled.div`
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

const SkeletonDifficultyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const SkeletonDifficultyCard = styled.div`
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

const SkeletonTable = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`

const SkeletonHeader = styled.div`
  height: 24px;
  background: #f3f4f6;
  margin-bottom: 16px;
  border-radius: 4px;
  width: 60%;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`

const SkeletonTableHeader = styled.div`
  background: #f9fafb;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 16px;
`

const SkeletonTableBody = styled.div`
  padding: 0;
`

const SkeletonRow = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  
  &:nth-child(even) {
    background: #f9fafb;
  }
`

const SkeletonCell = styled.div`
  height: 16px;
  background: #e5e7eb;
  border-radius: 4px;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
`

const SkeletonTopicTable = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-bottom: 24px;
`

const SkeletonTopicHeader = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 16px;
`

const SkeletonTopicRow = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr 1fr 1fr 1fr 1fr;
  gap: 16px;
`

const SkeletonLine = styled.div`
  height: ${props => props.height || '20px'};
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: ${props => props.marginBottom || '12px'};
  width: ${props => props.width || '100%'};
`

// Bottom Section
const BottomSection = styled.div`
  margin-top: auto;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`

const ThankYouText = styled.h2`
  font-size: 24px;
  font-weight: 500;
  color: #333333;
  margin-bottom: 16px;
`

const Message = styled.p`
  font-size: 16px;
  color: #666666;
  line-height: 1.6;
  margin-bottom: 24px;
`

const NextButton = styled.button`
  padding: 14px 36px;
  background: #4640de;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: #3530ca;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`

const Completion = ({ assessmentData, submittedTime, setAssessment, isPractice = false }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [remainingTime, setRemainingTime] = useState(60 * 60)
  const [showSolutions, setShowSolutions] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [isRetrying, setIsRetrying] = useState(false)
  // NEW: Add initial loading state
  const [initialLoading, setInitialLoading] = useState(true)

  // Redux selectors
  const studentData = useSelector((state) => state.studentData?.studentDetails)
  const currentAssessment = useSelector((state) => state.assessment.currentAssessment)
  const aptitudeScore = useSelector(selectors.getAptitudeScore)
  const aptitudeScoreLoading = useSelector(selectors.getAptitudeScoreLoading)
  const aptitudeScoreError = useSelector(selectors.getAptitudeScoreError)

  console.log("✨ Assessment Data:", assessmentData)
  console.log("✨ Current Assessment:", currentAssessment)

  const isMinusSystem =
    aptitudeScore?.isMinusSystem === true ||
    aptitudeScore?.IsMinusSystem === true ||
    aptitudeScore?.data?.isMinusSystem === true ||
    aptitudeScore?.data?.IsMinusSystem === true;

  const isCorporateAssessment = currentAssessment?.isCorporate === true || assessmentData?.isCorporate === true;

  // Enhanced fetch function with retry logic
  const fetchScoreWithRetry = async (assessmentAssignedId, attempt = 1) => {
    const maxRetries = 5
    const baseDelay = 3000 
    const maxDelay = 15000 

    try {
      const result = await dispatch(fetchAptitudeScore(assessmentAssignedId))
      
      if (result && result.id) {
        setRetryCount(0)
        setIsRetrying(false)
        return result
      } else {
        throw new Error("No data received")
      }
    } catch (error) {
      if (attempt < maxRetries) {
        const delay = Math.min(baseDelay * attempt, maxDelay)
        setRetryCount(attempt)
        setIsRetrying(true)
        
        setTimeout(() => {
          fetchScoreWithRetry(assessmentAssignedId, attempt + 1)
        }, delay)
      } else {
        setIsRetrying(false)
        setRetryCount(maxRetries)
      }
    }
  }

  // Manual retry function
  const handleManualRetry = () => {
    const assessmentAssignedId = currentAssessment?.assessment_assigned_id || assessmentData?.assessment_assigned_id
    if (assessmentAssignedId) {
      setRetryCount(0)
      setIsRetrying(true)
      fetchScoreWithRetry(assessmentAssignedId)
    }
  }

  // NEW: Add initial loading delay effect
  useEffect(() => {
    // Show initial loading for 3 seconds to prevent glitches
    const initialLoadingTimer = setTimeout(() => {
      setInitialLoading(false)
    }, 3000)

    return () => clearTimeout(initialLoadingTimer)
  }, [])

  // Fetch aptitude score on component mount with retry logic
  useEffect(() => {
    const assessmentAssignedId = currentAssessment?.assessment_assigned_id || assessmentData?.assessment_assigned_id
    
    if (assessmentAssignedId && !isPractice && !initialLoading) {
      // Only start fetching after initial loading is complete
      setTimeout(() => {
        fetchScoreWithRetry(assessmentAssignedId)
      }, 500) // Small delay after initial loading
    }
  }, [dispatch, currentAssessment, assessmentData, isPractice, initialLoading])

  // Exit fullscreen when component mounts
  useEffect(() => {
    document.body.classList.remove('assessment-fullscreen-mode')
    const navbar = document.querySelector('nav, .navbar, .header, .app-header, .top-nav')
    if (navbar) {
      navbar.style.display = ''
    }
    
    if (document.fullscreenElement) {
      try {
        document.exitFullscreen().catch((err) => console.error("Error exiting fullscreen on completion:", err))
      } catch (error) {
        console.error("Error in exitFullscreen on completion:", error)
      }
    }
  }, [])

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer)
          return 0
        }
        return prevTime - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  // Cleanup effect
  useEffect(() => {
    const cleanupTimer = setTimeout(() => {
      dispatch(resetResponses())
    }, 2000)

    return () => clearTimeout(cleanupTimer)
  }, [dispatch])

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`
  }

  const formatTimeDetailed = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }


  const handleNext = () => {
  document.body.classList.remove('assessment-fullscreen-mode')
  const navbar = document.querySelector('nav, .navbar, .header, .app-header, .top-nav')
  if (navbar) {
    navbar.style.display = ''
  }
  
  if (document.fullscreenElement) {
    try {
      document.exitFullscreen().catch((err) => console.error("Error exiting fullscreen:", err))
    } catch (error) {
      console.error("Error in exitFullscreen:", error)
    }
  }

  dispatch(resetResponses())

  if (isPractice) {
    localStorage.removeItem("isPracticeAssessment")
    navigate("/placementprep")
    setAssessment?.(null)
    return
  }

  navigate("/assessment")
  setAssessment?.(null)
}

  const handleShowSolutions = () => {
    setShowSolutions(true)
  }

  const handleBackFromSolutions = () => {
    setShowSolutions(false)
  }

  if (showSolutions) {
    return (
      <Solutions
        assessmentData={assessmentData || currentAssessment}
        onBack={handleBackFromSolutions}
      />
    )
  }

  // Calculate percentage for overall score
  const overallPercentage = aptitudeScore ? Math.round((aptitudeScore.gainedScore / aptitudeScore.totalScore) * 100) : 0
  const accuracy = aptitudeScore?.statistics?.summary ? 
    Math.round((aptitudeScore.statistics.summary.total_right / aptitudeScore.statistics.summary.total_questions) * 100) : 0

  // Calculate average time per question for each category
  const getAverageTimePerQuestion = (category) => {
    const totalQuestions = category.topics.reduce((sum, topic) => 
      sum + (topic.right + topic.wrong + topic.unattempted), 0
    )
    if (totalQuestions === 0) return 0
    return Math.round(category.time_spent_seconds / totalQuestions)
  }

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <ResultsSection>
      {/* Overview Section Skeleton */}
      <SkeletonOverviewGrid>
        {[1, 2, 3, 4].map(i => (
          <SkeletonScoreCard key={i}>
            <SkeletonLine height="32px" width="60%" marginBottom="8px" />
            <SkeletonLine height="16px" width="80%" marginBottom="4px" />
            <SkeletonLine height="12px" width="40%" marginBottom="0" />
          </SkeletonScoreCard>
        ))}
      </SkeletonOverviewGrid>

      <SkeletonSummaryGrid>
        {[1, 2, 3, 4, 5].map(i => (
          <SkeletonSummaryCard key={i}>
            <SkeletonLine height="24px" width="50%" marginBottom="8px" />
            <SkeletonLine height="12px" width="70%" marginBottom="0" />
          </SkeletonSummaryCard>
        ))}
      </SkeletonSummaryGrid>

      <div>
        <SkeletonLine height="20px" width="40%" marginBottom="16px" />
        <SkeletonDifficultyGrid>
          {[1, 2, 3].map(i => (
            <SkeletonDifficultyCard key={i}>
              <SkeletonLine height="16px" width="70%" marginBottom="12px" />
              <SkeletonLine height="28px" width="50%" marginBottom="8px" />
              <SkeletonLine height="14px" width="30%" marginBottom="0" />
            </SkeletonDifficultyCard>
          ))}
        </SkeletonDifficultyGrid>
      </div>

      {/* Subject Analysis Skeleton */}
      <div>
        <SkeletonHeader />
        <SkeletonTable>
          <SkeletonTableHeader>
            <SkeletonCell />
            <SkeletonCell />
            <SkeletonCell />
            <SkeletonCell />
            <SkeletonCell />
            <SkeletonCell />
          </SkeletonTableHeader>
          <SkeletonTableBody>
            {[1, 2, 3].map(i => (
              <SkeletonRow key={i}>
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
              </SkeletonRow>
            ))}
          </SkeletonTableBody>
        </SkeletonTable>
      </div>

      {/* Topic Breakdown Skeletons */}
      {[1, 2, 3].map(categoryIndex => (
        <div key={categoryIndex}>
          <SkeletonHeader />
          <SkeletonTopicTable>
            <SkeletonTableHeader>
              <SkeletonTopicHeader>
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
                <SkeletonCell />
              </SkeletonTopicHeader>
            </SkeletonTableHeader>
            <SkeletonTableBody>
              {[1, 2, 3, 4].map(topicIndex => (
                <SkeletonRow key={topicIndex}>
                  <SkeletonTopicRow>
                    <SkeletonCell />
                    <SkeletonCell />
                    <SkeletonCell />
                    <SkeletonCell />
                    <SkeletonCell />
                    <SkeletonCell />
                    <SkeletonCell />
                  </SkeletonTopicRow>
                </SkeletonRow>
              ))}
            </SkeletonTableBody>
          </SkeletonTopicTable>
        </div>
      ))}
    </ResultsSection>
  )

  return (
    <Container>
      <Header>
        <Title>{assessmentData?.title || currentAssessment?.title || "Aptitude Assessment"}</Title>
        {!isPractice && !isCorporateAssessment && (
          <SolutionsButton 
            onClick={handleShowSolutions}
            disabled={!aptitudeScore || initialLoading}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V11H13V7H7V19H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            View Solutions
          </SolutionsButton>
        )}
      </Header>

      <TopSection>
        <AssessmentName>
          {/* Assessment Name content */}
        </AssessmentName>
        
        {isMinusSystem && (
          <NegativeMarkingInfo>
            Negative marking enabled
          </NegativeMarkingInfo>
        )}
      </TopSection>

      {/* Show initial loading for first 3 seconds OR during data loading */}
      {(initialLoading || aptitudeScoreLoading || isRetrying) && !isPractice && (
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>
            {initialLoading 
              ? "Preparing your assessment results..." 
              : isRetrying 
                ? `Retrying... (Attempt ${retryCount}/5)` 
                : "Generating your performance report..."
            }
          </LoadingText>
        </LoadingContainer>
      )}

      {/* Show error state */}
      {!initialLoading && aptitudeScoreError && !isPractice && retryCount >= 5 && (
        <ErrorContainer>
          <ErrorTitle>Unable to Load Results</ErrorTitle>
          <ErrorMessage>
            We're having trouble processing your assessment results. Please try again or contact support if the problem persists.
          </ErrorMessage>
          <RetryButton onClick={handleManualRetry} disabled={isRetrying}>
            {isRetrying ? "Retrying..." : "Retry Loading Results"}
          </RetryButton>
        </ErrorContainer>
      )}

      {/* Show results when available and not loading */}
      {(!initialLoading && aptitudeScore && !isPractice && !isCorporateAssessment && !aptitudeScoreLoading && !isRetrying) && (
        <ResultsSection>
          {/* Results Overview Section */}
          <Message>Your detailed results are displayed below. You can view solutions to understand the correct approaches for each question.</Message>

          {/* Overall Score Cards */}
          <OverallScoreGrid>
            <ScoreCard>
              <ScoreValue>{overallPercentage}%</ScoreValue>
              <ScoreLabel>Overall Score</ScoreLabel>
              <ScoreSubtext>{aptitudeScore.gainedScore}/{aptitudeScore.totalScore}</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>{aptitudeScore?.statistics?.summary?.total_right || 0}</ScoreValue>
              <ScoreLabel>Correct Answers</ScoreLabel>
              <ScoreSubtext>{accuracy}% accuracy</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>{aptitudeScore?.statistics?.summary?.total_attempted || 0}</ScoreValue>
              <ScoreLabel>Questions Attempted</ScoreLabel>
              <ScoreSubtext>out of {aptitudeScore?.statistics?.summary?.total_questions || 0}</ScoreSubtext>
            </ScoreCard>
            <ScoreCard>
              <ScoreValue>{formatTimeDetailed(aptitudeScore?.statistics?.summary?.total_time_seconds || 0)}</ScoreValue>
              <ScoreLabel>Time taken</ScoreLabel>
              <ScoreSubtext>{(aptitudeScore?.statistics?.summary?.average_speed_seconds_per_question || 0).toFixed(1)}s avg per question</ScoreSubtext>
            </ScoreCard>
          </OverallScoreGrid>

          {/* Summary Stats */}
          <SummaryGrid>
            <SummaryCard>
              <SummaryValue>{aptitudeScore?.statistics?.summary?.total_questions || 0}</SummaryValue>
              <SummaryLabel>Total Questions</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{aptitudeScore?.statistics?.summary?.total_right || 0}</SummaryValue>
              <SummaryLabel>Correct</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{aptitudeScore?.statistics?.summary?.total_wrong || 0}</SummaryValue>
              <SummaryLabel>Incorrect</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{aptitudeScore?.statistics?.summary?.total_unattempted || 0}</SummaryValue>
              <SummaryLabel>Unattempted</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue>{formatTimeDetailed(aptitudeScore?.statistics?.summary?.total_time_seconds || 0)}</SummaryValue>
              <SummaryLabel>Total Time</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          {/* Performance by Difficulty Level */}
          <DifficultySection>
            <SectionTitle>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Performance by Difficulty Level
            </SectionTitle>
            <DifficultyGrid>
              <DifficultyCard borderColor="#bbf7d0" bgColor="#f0fdf4">
                <DifficultyLabel>Easy Questions</DifficultyLabel>
                <DifficultyScore color="#16a34a">
                  {aptitudeScore?.statistics?.summary?.easy_gained || 0} / {aptitudeScore?.statistics?.summary?.easy_total || 0}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore?.statistics?.summary?.easy_total > 0 ? 
                    Math.max(0, Math.round((aptitudeScore?.statistics?.summary?.easy_gained / aptitudeScore?.statistics?.summary?.easy_total) * 100)) : 0}%
                </DifficultyPercentage>
                <DifficultyPercentage>
                  {aptitudeScore?.avgTimePerDifficulty?.easy ? `${aptitudeScore?.avgTimePerDifficulty?.easy}s` : '0'} avg per question
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fed7aa" bgColor="#fffbeb">
                <DifficultyLabel>Medium Questions</DifficultyLabel>
                <DifficultyScore color="#ea580c">
                  {aptitudeScore?.statistics?.summary?.medium_gained || 0} / {aptitudeScore?.statistics?.summary?.medium_total || 0}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore?.statistics?.summary?.medium_total > 0 ? 
                    Math.max(0, Math.round((aptitudeScore?.statistics?.summary?.medium_gained / aptitudeScore?.statistics?.summary?.medium_total) * 100)) : 0}%
                </DifficultyPercentage>
                <DifficultyPercentage>
                  {aptitudeScore?.avgTimePerDifficulty?.medium ? `${aptitudeScore?.avgTimePerDifficulty?.medium}s` : '0'} avg per question
                </DifficultyPercentage>
              </DifficultyCard>
              <DifficultyCard borderColor="#fecaca" bgColor="#fef2f2">
                <DifficultyLabel>Hard Questions</DifficultyLabel>
                <DifficultyScore color="#dc2626">
                  {aptitudeScore?.statistics?.summary?.hard_gained || 0} / {aptitudeScore?.statistics?.summary?.hard_total || 0}
                </DifficultyScore>
                <DifficultyPercentage>
                  {aptitudeScore?.statistics?.summary?.hard_total > 0 ? 
                    Math.max(0, Math.round((aptitudeScore?.statistics?.summary?.hard_gained / aptitudeScore?.statistics?.summary?.hard_total) * 100)) : 0}%
                </DifficultyPercentage>
                <DifficultyPercentage>
                  {aptitudeScore?.avgTimePerDifficulty?.hard ? `${aptitudeScore?.avgTimePerDifficulty?.hard}s` : '0'} avg per question
                </DifficultyPercentage>
              </DifficultyCard>
            </DifficultyGrid>
          </DifficultySection>

          {/* Detailed Subject Analysis Table */}
          <div>
            <SectionHeader>Detailed Subject Analysis</SectionHeader>
            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>Subject</TableHeaderCell>
                    <TableHeaderCell>Percentage</TableHeaderCell>
                    <TableHeaderCell>Score</TableHeaderCell>
                    <TableHeaderCell>Attempted / Total</TableHeaderCell>
                    <TableHeaderCell>Correct</TableHeaderCell>
                    <TableHeaderCell>Time Per Question</TableHeaderCell>
                  </tr>
                </TableHeader>
                <TableBody>
                  {aptitudeScore?.statistics?.categories?.map((category, index) => {
                    const totalQuestions = category.topics.reduce((sum, topic) =>
                      sum + (topic.right + topic.wrong + topic.unattempted), 0
                    )
                    // Attempted = answered questions only (right + wrong); unvisited/skipped are NOT attempted
                    const totalAttempted = category.topics.reduce((sum, topic) => sum + (topic.right + topic.wrong), 0)
                    const totalCorrect = category.topics.reduce((sum, topic) => sum + topic.right, 0)
                    const percentage = category.total_marks > 0 ? 
                      Math.round((category.gained_marks / category.total_marks) * 100) : 0
                    const avgTimePerQuestion = getAverageTimePerQuestion(category)

                    return (
                      <TableRow key={index}>
                        <TableCell>{category.name}</TableCell>
                        <PercentageCell percentage={percentage}>{percentage}%</PercentageCell>
                        <ScoreCell>{category.gained_marks} / {category.total_marks}</ScoreCell>
                        <TableCell>{totalAttempted} / {totalQuestions}</TableCell>
                        <TableCell>{totalCorrect}</TableCell>
                        <TableCell>{avgTimePerQuestion}s</TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Subject-wise Topic Breakdown Tables */}
          {aptitudeScore?.statistics?.categories?.map((category, categoryIndex) => (
            <div key={categoryIndex}>
              <SectionHeader>{category.name} - Topic Breakdown</SectionHeader>
              <TableContainer>
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHeaderCell>Topic</TableHeaderCell>
                      <TableHeaderCell>Percentage</TableHeaderCell>
                      <TableHeaderCell>Score</TableHeaderCell>
                      <TableHeaderCell>Attempted</TableHeaderCell>
                      <TableHeaderCell>Correct</TableHeaderCell>
                      <TableHeaderCell>Wrong</TableHeaderCell>
                      <TableHeaderCell>Skipped</TableHeaderCell>
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {category.topics.map((topic, topicIndex) => {
                      const percentage = topic.total_marks > 0 ? 
                        Math.round((topic.gained_marks / topic.total_marks) * 100) : 0
                      const attempted = topic.right + topic.wrong

                      return (
                        <TableRow key={topicIndex}>
                          <TableCell>{topic.name}</TableCell>
                          <PercentageCell percentage={percentage}>{percentage}%</PercentageCell>
                          <ScoreCell>{topic.gained_marks} / {topic.total_marks}</ScoreCell>
                          <TableCell>{attempted}</TableCell>
                          <TableCell>{topic.right}</TableCell>
                          <TableCell>{topic.wrong}</TableCell>
                          <TableCell>{topic.unattempted}</TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          ))}
        </ResultsSection>
      )}

      {/* Show practice mode content immediately for practice assessments after initial loading
      {(!initialLoading && isPractice) && (
        <ResultsSection>
          <Message>This was a practice assessment to help you prepare. You can take more practice assessments to improve your skills.</Message>
        </ResultsSection>
      )} */}

      {(!initialLoading && !isPractice && isCorporateAssessment) && (
        <ResultsSection>
          <ThankYouText>Thank you for completing the assessment!</ThankYouText>
          <Message>Your result is being processed and will be available shortly. Please check the Completed Assessment tab to view your score.</Message>
        </ResultsSection>
      )}

      <BottomSection>
        <NextButton onClick={handleNext}>
          Return to Assessments
        </NextButton>
      </BottomSection>
    </Container>
  )
}

export default Completion
